import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListMarketingEventsResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/** Webinars and events, with who registered and who came (F-083). The way into SC-66. */
export function EventsPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const enabled = useEntitlement('F-083');
  const { can } = useSession();
  const events = useQuery<ListMarketingEventsResult, ApiError>({
    queryKey: ['marketing-events'],
    queryFn: () => api.listMarketingEvents({}),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled) return null;
  return (
    <Panel data-region="marketing-events">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('events.title')}</SectionLabel>
          {can('campaign.send') ? (
            <Link data-event-new to="/events/new" className="ms-auto text-dense text-bronze-text hover:text-ink">
              {t('events.new')}
            </Link>
          ) : null}
        </div>
        {events.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : events.isError ? (
          <p className="text-dense text-danger-text">{errorText(events.error)}</p>
        ) : events.data.events.length === 0 ? (
          <p data-events-none className="text-meta text-ink-meta">{t('events.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {events.data.events.map((event) => (
              <li key={event.eventId} data-event-row={event.slug} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                {can('campaign.send') ? (
                  <Link to={`/events/${event.eventId}`} className="text-dense text-ink hover:text-bronze-text">{event.name}</Link>
                ) : (
                  <span className="text-dense text-ink">{event.name}</span>
                )}
                <span className="text-meta text-ink-meta">{formatDateTime(event.startsAt, format)}</span>
                <span className="text-meta text-ink-meta">{t(`events.status.${event.status}`)}</span>
                <span className="ms-auto text-meta text-ink-meta">
                  {t('events.rowCounts', {
                    values: { registered: formatCount(event.funnel.registered, format), attended: formatCount(event.funnel.attended, format) },
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
